/**
 * Klaviyo API client for back-in-stock subscriptions
 */

import {
  PUBLIC_KLAVIYO_COMPANY_ID_SV,
  PUBLIC_KLAVIYO_COMPANY_ID_DA,
  PUBLIC_KLAVIYO_COMPANY_ID_NB,
  PUBLIC_KLAVIYO_COMPANY_ID_FI,
  PUBLIC_KLAVIYO_COMPANY_ID_EN,
  PUBLIC_KLAVIYO_COMPANY_ID_ES
} from '$env/static/public';

export interface BackInStockSubscription {
  email: string;
  productSku: string;
  language: string;
}

export interface KlaviyoResponse {
  success?: boolean;
  message?: string;
}

/**
 * Company IDs for different languages
 */
const COMPANY_IDS = {
  'sv': PUBLIC_KLAVIYO_COMPANY_ID_SV,
  'da': PUBLIC_KLAVIYO_COMPANY_ID_DA,
  'nb': PUBLIC_KLAVIYO_COMPANY_ID_NB,
  'fi': PUBLIC_KLAVIYO_COMPANY_ID_FI,
  'en': PUBLIC_KLAVIYO_COMPANY_ID_EN,
  'es': PUBLIC_KLAVIYO_COMPANY_ID_ES,
};

/**
 * Creates a back-in-stock subscription via Klaviyo API
 * @param fetch - The fetch function to use for the request
 * @param subscription - The subscription data
 * @returns A promise that resolves when the subscription is created
 * @throws Error with user-friendly message on failure
 */
export const createBackInStockSubscription = async (
  fetch: typeof window.fetch,
  subscription: BackInStockSubscription
): Promise<void> => {
  try {
    const { email, productSku, language } = subscription;
    const companyId = getKlaviyoCompanyId(language);

    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-07-15'
        },
        body: JSON.stringify({
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: email,
                  }
                }
              },
              channels: ["EMAIL"],
            },
            relationships: {
              variant: {
                data: {
                  type: "catalog-variant",
                  id: '$custom:::$default:::' + productSku,
                }
              }
            }
          }
        }),
      }
    );

    const responseText = await response.text();

    // Handle empty response (common for 202 Accepted responses)
    if (!responseText || responseText.length === 0) {
      return;
    }

    let result: KlaviyoResponse;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      // Don't expose technical JSON parsing errors to users
      throw new Error('subscription_failed');
    }

    // Handle JSON response - throw generic error for any failure
    if (result.success === false) {
      throw new Error('subscription_failed');
    }
  } catch (error: any) {
    // Only throw user-friendly error codes, never technical details
    if (error.message === 'subscription_failed') {
      throw error;
    }

    // For any other error (network, etc.), throw generic user-friendly error
    throw new Error('subscription_failed');
  }
};

/**
 * Gets the appropriate Klaviyo company ID based on language
 * @param language - The language code (e.g., 'sv', 'en', 'da')
 * @returns The company ID for the given language, falling back to English or default
 */
export const getKlaviyoCompanyId = (language: string): string => {
  const defaultCompanyId = 'RXAwRs';

  return COMPANY_IDS[language as keyof typeof COMPANY_IDS] ||
         COMPANY_IDS['en'] ||
         defaultCompanyId;
};